import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { logout } from '../../services/authService';
import { subscribeToUserProfile } from '../../services/userService';
import {
  requestAdminMode,
  checkSiteAdminEligibility
} from '../../services/adminModeService';
import { trackNavigation, trackButtonClick } from '../../utils/analytics';
import Modal from '../Modal';
import { SignIn, SignUp } from '../Auth';
import ForgotPassword from '../Auth/ForgotPassword';
import ForgotUsername from '../Auth/ForgotUsername';
import UserProfileComponent from '../UserProfile';
import ProfileSetup from '../ProfileSetup';
import NotificationBell from '../NotificationBell';
import AdminModeIndicator from '../AdminModeIndicator';
import './Header.css';
import type { UserProfile } from '../../types/user';

type AuthModalType = 'signin' | 'signup' | 'profile' | 'profile-setup' | 'forgot-password' | 'forgot-username' | null;

export default function Header() {
  const { t } = useTranslation();
  const { currentUser, adminMode, refreshAdminMode } = useAuth();
  const [authModal, setAuthModal] = useState<AuthModalType>(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [canRequestSystemAdmin, setCanRequestSystemAdmin] = useState(false);
  const [requestingAdmin, setRequestingAdmin] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Computed property - derive from profile state
  const isProfileIncomplete = profile?.metadata.profileCompleted !== true;

  const handleNavClick = (section: string, sectionName: string) => {
    trackNavigation('home', section, 'header_nav_click');
    console.debug(`Navigating to ${sectionName} section`);
  };

  const handleSignUpClick = () => {
    trackButtonClick('sign_up', 'header');
    setAuthModal('signup');
  };

  const handleSignInClick = () => {
    trackButtonClick('sign_in', 'header');
    setAuthModal('signin');
  };

  const handleProfileClick = () => {
    setShowUserMenu(false);
    setAuthModal('profile');
  };

  const handleSignOut = async () => {
    try {
      await logout();
      setShowUserMenu(false);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const handleRequestAdminMode = async () => {
    if (requestingAdmin || adminMode.isActive) return;

    setRequestingAdmin(true);
    try {
      console.log('Admin mode requested:', {
        userId: currentUser?.uid,
        email: currentUser?.email,
        displayName: currentUser?.displayName,
        adminType: 'system'
      });

      await requestAdminMode({ adminType: 'system' });
      await refreshAdminMode();
      setShowUserMenu(false);

      console.log('Admin mode granted:', {
        userId: currentUser?.uid,
        email: currentUser?.email,
        displayName: currentUser?.displayName,
        adminType: 'system'
      });

      // Refresh page to apply admin status
      window.location.reload();
    } catch (error) {
      console.error('Error requesting admin mode:', error);
      alert(error instanceof Error ? error.message : 'Failed to request admin mode');
    } finally {
      setRequestingAdmin(false);
    }
  };

  const handleSignUpSuccess = () => {
    // After successful signup, show profile setup
    setAuthModal('profile-setup');
  };

  const handleProfileSetupComplete = async () => {
    // Profile will update via subscription, just close modal
    setAuthModal(null);
  };

  const closeModal = () => {
    setAuthModal(null);
  };

  const getModalTitle = () => {
    switch (authModal) {
      case 'signin':
        return t('auth.signIn');
      case 'signup':
        return t('auth.signUp');
      case 'profile':
        return t('profile.title');
      case 'profile-setup':
        return 'Complete Your Profile';
      case 'forgot-password':
        return t('auth.forgotPassword');
      case 'forgot-username':
        return t('auth.forgotUsername');
      default:
        return '';
    }
  };

  // Subscribe to user profile and check completion status in real-time
  useEffect(() => {
    if (!currentUser) {
      return;
    }

    // Subscribe to real-time profile updates
    const unsubscribe = subscribeToUserProfile(
      currentUser.uid,
      (userProfile) => {
        setProfile(userProfile);
      },
      (error) => {
        console.error('Error subscribing to user profile:', error);
      }
    );

    // Cleanup subscription on unmount or when user changes
    return () => {
      unsubscribe();
      setProfile(null);
    };
  }, [currentUser]);

  // Check if user is eligible for system admin
  useEffect(() => {
    if (!currentUser) {
      setCanRequestSystemAdmin(false);
      return;
    }

    checkSiteAdminEligibility(currentUser.uid).then((eligible) => {
      setCanRequestSystemAdmin(eligible);
    });
  }, [currentUser]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  return (
    <header className="header">
      <div className="header-container">
        <div className="header-logo">
          <h1>{t('header.title')}</h1>
        </div>
        <nav className="header-nav">
          <Link to="/#hero" onClick={() => handleNavClick('hero', 'home')}>{t('header.nav.home')}</Link>
          <Link to="/#features" onClick={() => handleNavClick('features', 'about')}>{t('header.nav.about')}</Link>
          <Link to="/o" onClick={() => handleNavClick('organizations', 'organizations')}>{t('header.nav.organizations')}</Link>

          {currentUser ? (
            <>
              <AdminModeIndicator />
              <NotificationBell />
              <div className="user-menu-container" ref={menuRef}>
                <button
                  type="button"
                  className="user-menu-button"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  aria-haspopup="menu"
                >
                  <span className="user-name">{currentUser.displayName || currentUser.email}</span>
                  <svg className="chevron-icon" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  {isProfileIncomplete && (
                    <span className="notification-badge" title="Complete your profile"></span>
                  )}
                </button>

                {showUserMenu && (
                  <div className="user-menu-dropdown" role="menu">
                    <button type="button" onClick={handleProfileClick} role="menuitem">
                      <span className={isProfileIncomplete ? 'menu-item-with-badge' : ''}>
                        {t('header.nav.profile')}
                        {isProfileIncomplete && <span className="menu-item-badge"></span>}
                      </span>
                    </button>
                    {canRequestSystemAdmin && !adminMode.isActive && (
                      <button
                        type="button"
                        onClick={handleRequestAdminMode}
                        role="menuitem"
                        disabled={requestingAdmin}
                        className="admin-request-button"
                      >
                        {requestingAdmin ? t('adminMode.requesting') : t('adminMode.requestSystem')}
                      </button>
                    )}
                    <button type="button" onClick={handleSignOut} role="menuitem">
                      {t('header.nav.signOut')}
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <button type="button" className="signin-button" onClick={handleSignInClick}>
                {t('auth.signIn')}
              </button>
              <button type="button" className="signup-button" onClick={handleSignUpClick}>
                {t('header.nav.signUp')}
              </button>
            </>
          )}
        </nav>
      </div>

      <Modal isOpen={authModal !== null} onClose={closeModal} title={getModalTitle()}>
        {authModal === 'signin' && (
          <SignIn
            onClose={closeModal}
            onSwitchToSignUp={() => setAuthModal('signup')}
            onSwitchToForgotPassword={() => setAuthModal('forgot-password')}
            onSwitchToForgotUsername={() => setAuthModal('forgot-username')}
          />
        )}
        {authModal === 'signup' && (
          <SignUp
            onSignUpSuccess={handleSignUpSuccess}
            onClose={closeModal}
            onSwitchToSignIn={() => setAuthModal('signin')}
          />
        )}
        {authModal === 'forgot-password' && (
          <ForgotPassword
            onClose={closeModal}
            onBackToSignIn={() => setAuthModal('signin')}
          />
        )}
        {authModal === 'forgot-username' && (
          <ForgotUsername
            onClose={closeModal}
            onBackToSignIn={() => setAuthModal('signin')}
          />
        )}
        {authModal === 'profile' && (
          <UserProfileComponent
            onClose={closeModal}
            onProfileStatusChange={() => { }}
          />
        )}
        {authModal === 'profile-setup' && (
          <ProfileSetup onComplete={handleProfileSetupComplete} />
        )}
      </Modal>
    </header>
  );
}
